import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import * as assignReferredBetslip from 'src/modules/betslip/actions/assign-referred-betslip';
import {
    BetReferralNotificationType,
    ChatRequestNotificationType,
    GamstopPositiveNotificationType,
    NotificationMainChannelType,
} from 'src_server/types/notificationData';
import { filterVisibleGamstopPositiveNotifications } from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/gamstopNotificationUtils';
import { ApiResponseWrapper } from 'src_common/common/mobx-utils/ApiResponseWrapper';
import { WebsocketV1 } from 'src/AppState/websocketV1/WebsocketV1';
import { DateTime } from 'src_common/utils/time/time';
import { timeout } from 'src_common/common/mobx-utils/timeout';

export class NotificationsCentreState {
    @observable public openChatList: boolean = false;
    @observable public openReferredList: boolean = false;
    @observable public currentNumberOfNotifications: number = 0;
    @observable public prevChatPendingNumber: number = 0;
    @observable public prevReferredPendingNumber: number = 0;
    private readonly webSocketV1: WebsocketV1;
    public notifications: ApiResponseWrapper<NotificationMainChannelType>;

    public static get = AutoWeakMap.create((common: Common) => new NotificationsCentreState(common));

    private constructor(private common: Common) {
        makeAutoObservable(this);
        this.webSocketV1 = WebsocketV1.get(this.common);

        this.notifications = new ApiResponseWrapper(
            'Notification:main',
            [],
            async (): Promise<NotificationMainChannelType> => {
                await timeout(1500); //Temporary solution - waiting for user JWT. Without first request is with anonymous jwt
                return await $appState.trpcClient.client.notification.getMainNotifications.query();
            },
            (refresh) => {
                const notificationTopic = 'S:Notification:main';
                refresh();

                window.addEventListener('online', refresh);
                this.webSocketV1.subscribe(notificationTopic, refresh);

                return (): void => {
                    window.removeEventListener('online', refresh);
                    this.webSocketV1.unsubscribe(notificationTopic, refresh);
                };
            }
        );
    }

    @action public toggleChatList = (): void => {
        this.openReferredList = false;
        this.openChatList = !this.openChatList;
    };

    @action public toggleReferralList = (): void => {
        this.openChatList = false;
        this.openReferredList = !this.openReferredList;
    };

    private onAssignTrader = (userId: number): void => {
        $appState.reduxAction.dispatchAction(assignReferredBetslip.request(userId));
    };

    @computed public get chatPendingNumber(): number {
        const chatNotifications = this.notifications.data;
        let chatPendingNumber = 0;
        for (const chatNotification of chatNotifications) {
            if (chatNotification.type === 'chat-request' && chatNotification.status === 'pending') {
                chatPendingNumber++;
            }
        }
        return chatPendingNumber;
    }

    @computed public get referredPendingNumber(): number {
        const referredNotifications = this.notifications.data;
        let referredPendingNumber = 0;
        for (const referredNotification of referredNotifications) {
            if (referredNotification.type === 'bet-referral' && referredNotification.status === 'pending') {
                referredPendingNumber++;
            }
        }
        return referredPendingNumber;
    }

    @computed private get notificationsList(): NotificationMainChannelType {
        const notifications = this.notifications.data;
        return notifications.sort((a, b): number => {
            const createdAtA = DateTime.from(a.createdAt)?.unixMs();
            const createdAtB = DateTime.from(b.createdAt)?.unixMs();
            if (createdAtA === undefined || createdAtB === undefined) {
                return 0;
            }
            return createdAtB - createdAtA;
        });
    }

    @computed public get chatNotifications(): Array<ChatRequestNotificationType> {
        const chatNotifications: Array<ChatRequestNotificationType> = [];
        for (const notification of this.notificationsList) {
            if (notification.type === 'chat-request') {
                chatNotifications.push(notification);
            }
        }

        return chatNotifications;
    }

    @computed public get betReferralNotifications(): Array<BetReferralNotificationType> {
        const betReferralNotifications: Array<BetReferralNotificationType> = [];
        for (const notification of this.notificationsList) {
            if (notification.type === 'bet-referral') {
                betReferralNotifications.push(notification);
            }
        }
        return betReferralNotifications;
    }

    @computed public get gamstopPositiveNotifications(): Array<GamstopPositiveNotificationType> {
        const gamstopPositiveNotifications: Array<GamstopPositiveNotificationType> = [];
        for (const notification of this.notificationsList) {
            if (notification.type === 'gamstop-positive') {
                gamstopPositiveNotifications.push(notification);
            }
        }
        return filterVisibleGamstopPositiveNotifications(gamstopPositiveNotifications);
    }

    @action public onAssignTraderHandle = (
        userId: number | undefined,
        assignee: BetReferralNotificationType['details']['assignee']
    ): void => {
        if (userId !== undefined && assignee === null) {
            this.onAssignTrader(userId);
        }
        this.openReferredList = false;
        this.openChatList = false;
    };

    public showDesktopNotification = async (count: number, type: 'chat' | 'betrefferal'): Promise<void> => {
        if ('Notification' in window) {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const notificationTitle = `There is the ${type === 'chat' ? 'new chat' : 'new betrefferal'} request`;
                new Notification(notificationTitle, {
                    body: `You have ${count} new notifications`,
                });
            }
        } else {
            console.warn('There is no notification API in this browser');
        }
    };
}
